import { NO_FILL } from './MarkupDrawController'
import { ITextMarkupSpec, textMarkupFont } from './markupTextStyle'
import { ITextLayout } from './textMarkupLayout'

import Colors from '~/client/src/shared/theme.module.scss'

export interface ITextRaster {
  canvas: HTMLCanvasElement
  // Device px per layout px actually used (lowered when the side cap applies).
  ratio: number
}

// Chrome's automatic underline in device px (it ignores the font's post table), on whole
// pixels below the pixel-snapped baseline: thickness floor(size·ratio/10), its top
// ceil(round(size/10)/2) CSS px down; both at least 1.
export function underlinePx(
  fontSize: number,
  ratio: number,
): { offset: number; thickness: number } {
  const offsetCss = Math.max(1, Math.ceil(Math.round(fontSize / 10) / 2))
  return {
    offset: Math.round(offsetCss * ratio),
    thickness: Math.max(1, Math.floor((fontSize * ratio) / 10)),
  }
}

export function rasterRatio(
  layout: ITextLayout,
  ratio: number,
  maxSidePx: number,
): number {
  const longest = Math.max(layout.width, layout.height) * ratio
  return longest > maxSidePx ? (ratio * maxSidePx) / longest : ratio
}

// The copy-select ring around a box: 2px outside its edge, rounded 2px, in the primary
// colour (a canvas cannot read var(--primary)).
export const TEXT_RING_PX = 2

// Chrome's accelerated canvas spends 10–25 ms the first time it draws glyphs from about 162 up
// to 256 device px; a software canvas draws them in under 8 ms. From 256 px the accelerated one
// is the faster, under 4 ms with the upload (D1, 2026-10-26).
const SOFTWARE_GLYPH_PX = { above: 160, below: 256 }

let paintCanvas: HTMLCanvasElement | null = null
let softwareCanvas: HTMLCanvasElement | null = null
let underlineCanvas: HTMLCanvasElement | null = null
let inkCanvas: HTMLCanvasElement | null = null

function sized(
  canvas: HTMLCanvasElement | null,
  w: number,
  h: number,
): HTMLCanvasElement {
  const c = canvas ?? document.createElement('canvas')
  c.width = w
  c.height = h
  return c
}

// Paints the box the saved view shows (fill, glyphs at their measured positions,
// underline) at `ratio` device px per layout px, centred in a whole-pixel canvas.
// The canvases are shared: upload one before painting the next text.
export function paintTextMarkup(
  layout: ITextLayout,
  spec: ITextMarkupSpec,
  ratio: number,
  maxSidePx: number,
): ITextRaster {
  const r = rasterRatio(layout, ratio, maxSidePx)
  const w = Math.max(1, Math.ceil(layout.width * r))
  const h = Math.max(1, Math.ceil(layout.height * r))
  const dx = (w - layout.width * r) / 2
  const dy = (h - layout.height * r) / 2
  const font = textMarkupFont(spec)
  const drawText = (c: CanvasRenderingContext2D, color: string): void => {
    c.setTransform(r, 0, 0, r, dx, dy)
    c.font = font
    c.textBaseline = 'alphabetic'
    c.textAlign = 'left'
    c.fillStyle = color
    layout.lines.forEach(line =>
      line.segments.forEach(seg =>
        c.fillText(seg.text, seg.left, line.baseline),
      ),
    )
  }

  const glyphPx = spec.fontSize * r
  const software =
    glyphPx > SOFTWARE_GLYPH_PX.above && glyphPx < SOFTWARE_GLYPH_PX.below
  const canvas = software
    ? (softwareCanvas = sized(softwareCanvas, w, h))
    : (paintCanvas = sized(paintCanvas, w, h))
  const ctx = canvas.getContext('2d', { willReadFrequently: software })
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, w, h)
  if (spec.fillColor !== NO_FILL) {
    ctx.setTransform(r, 0, 0, r, dx, dy)
    ctx.fillStyle = spec.fillColor
    ctx.fillRect(0, 0, layout.width, layout.height)
  }
  drawText(ctx, spec.color)

  if (spec.underline) {
    const { offset, thickness } = underlinePx(spec.fontSize, r)
    const bands = layout.lines.map(line => {
      const top = Math.round(dy + line.baseline * r) + offset
      return { line, y0: Math.max(0, top), y1: Math.min(h, top + thickness) }
    })
    underlineCanvas = sized(underlineCanvas, w, h)
    // Software too: an accelerated one costs a read-back to land on the software canvas.
    const u = underlineCanvas.getContext('2d', { willReadFrequently: true })
    u.fillStyle = spec.color
    bands.forEach(({ line, y0, y1 }) =>
      u.fillRect(dx + line.left * r, y0, (line.right - line.left) * r, y1 - y0),
    )
    // Skip-ink like the DOM: cut the line wherever glyph ink crosses it, grown by
    // the thickness.
    inkCanvas = sized(inkCanvas, w, h)
    const ink = inkCanvas.getContext('2d', { willReadFrequently: true })
    drawText(ink, '#000')
    const grow = thickness
    bands.forEach(({ y0, y1 }) => {
      if (y1 <= y0) return
      const band = ink.getImageData(0, y0, w, y1 - y0).data
      for (let x = 0; x < w; x++) {
        for (let y = 0; y < y1 - y0; y++) {
          if (band[(y * w + x) * 4 + 3] > 64) {
            u.clearRect(x - grow, y0 - 1, 2 * grow + 1, y1 - y0 + 2)
            break
          }
        }
      }
    })
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.drawImage(underlineCanvas, 0, 0)
  }
  return { canvas, ratio: r }
}

// The ring alone, sized to the box plus the ring on every side and centred like the box, so
// it draws as its own quad on the same anchor.
export function paintTextMarkupRing(
  layout: ITextLayout,
  ratio: number,
  maxSidePx: number,
): ITextRaster {
  const outer = {
    ...layout,
    width: layout.width + 2 * TEXT_RING_PX,
    height: layout.height + 2 * TEXT_RING_PX,
  }
  const r = rasterRatio(outer, ratio, maxSidePx)
  const w = Math.max(1, Math.ceil(outer.width * r))
  const h = Math.max(1, Math.ceil(outer.height * r))
  paintCanvas = sized(paintCanvas, w, h)
  const ctx = paintCanvas.getContext('2d')
  ctx.setTransform(
    r,
    0,
    0,
    r,
    (w - outer.width * r) / 2,
    (h - outer.height * r) / 2,
  )
  ctx.strokeStyle = Colors.brandBlue
  ctx.lineWidth = TEXT_RING_PX
  ctx.beginPath()
  ctx.roundRect(
    TEXT_RING_PX / 2,
    TEXT_RING_PX / 2,
    outer.width - TEXT_RING_PX,
    outer.height - TEXT_RING_PX,
    TEXT_RING_PX,
  )
  ctx.stroke()
  return { canvas: paintCanvas, ratio: r }
}
